import React, { useState, useEffect } from 'react';
import { RISK_TIERS, getHeroHeadline, getGuidance } from '../../../utils/riskTier';

// ── Hero risk scale — spec-exact tokens (separate from RISK_TIERS card-background palette) ──
const SCALE_TC = {
  baseline: { text: '#15803d', lightFill: '#f0fdf4', border: '#86efac' },
  elevated: { text: '#a16207', lightFill: '#fefce8', border: '#fde047' },
  advisory: { text: '#c2410c', lightFill: '#fff7ed', border: '#fb923c' },
  warning:  { text: '#b91c1c', lightFill: '#fef2f2', border: '#f87171' },
};
const SCALE_ORDER  = ['baseline', 'elevated', 'advisory', 'warning'];
const SCALE_LABELS = { baseline: 'Baseline', elevated: 'Elevated', advisory: 'Advisory', warning: 'Warning' };

function useSmallScreen(breakpoint = 760) {
  const [small, setSmall] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setSmall(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return small;
}

function ScaleCaret({ dir, color }) {
  const pos = dir > 0 ? { top: -9, right: 10 } : { top: -9, left: 10 };
  const rotate = dir > 0 ? 45 : 225;
  return (
    <svg
      aria-hidden="true"
      width="16" height="16" viewBox="0 0 16 16"
      style={{ position: 'absolute', ...pos, transform: `rotate(${rotate}deg)` }}
    >
      <line x1="8" y1="14" x2="8" y2="3" stroke={color} strokeWidth="2.6" strokeLinecap="round" />
      <polyline points="3,8 8,3 13,8" fill="none" stroke={color} strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function HeroRiskScale({ risk, trend, showCaret = true }) {
  const small = useSmallScreen(760);
  const activeIdx = SCALE_ORDER.indexOf(risk);
  const dir = trend === 'rising' ? 1 : trend === 'declining' ? -1 : 0;

  return (
    <div
      role="img"
      aria-label={`Risk scale: ${SCALE_LABELS[risk] ?? risk}, step ${activeIdx + 1} of 4`}
      style={{ display: 'flex', alignItems: 'flex-end', gap: small ? 4 : 6, marginBottom: 20 }}
    >
      {SCALE_ORDER.map((k, i) => {
        const active = i === activeIdx;
        const tc = SCALE_TC[k];
        const showThisCaret = showCaret && active && dir !== 0
          && !(dir > 0 && i >= 3)
          && !(dir < 0 && i <= 0);
        return (
          <div
            key={k}
            style={{
              flex: 1, minWidth: 0, position: 'relative',
              height: active ? 50 : 42,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 9,
              background: active ? tc.text : '#f1efe9',
              border: `1px solid ${active ? tc.text : '#e7e2d6'}`,
              boxShadow: active ? '0 2px 8px rgba(31,42,46,0.14)' : 'none',
              transition: 'all .2s',
            }}
          >
            <span style={{
              fontSize: small ? 11 : (active ? 15 : 13.5),
              fontWeight: active ? 700 : 600,
              color: active ? '#ffffff' : '#9a9384',
              userSelect: 'none',
            }}>
              {SCALE_LABELS[k]}
            </span>
            {showThisCaret && <ScaleCaret dir={dir} color={tc.text} />}
          </div>
        );
      })}
    </div>
  );
}

const TREND_CHIP = {
  rising:   { bg: '#fef2f2', color: '#dc2626', text: '↑ Increasing' },
  declining:{ bg: '#f0fdf4', color: '#16a34a', text: '↓ Decreasing' },
  stable:   { bg: '#f8fafc', color: '#64748b', text: '→ Stable' },
};

const changeToWords = (pct) => {
  if (pct == null) return '';
  const abs = Math.abs(Math.round(pct));
  const dir = pct > 0 ? 'up' : 'down';
  if (abs < 5)  return ' — nearly unchanged since yesterday';
  if (abs < 20) return ` — ${dir} slightly since yesterday`;
  if (abs < 35) return ` — ${dir} about a fifth since yesterday`;
  if (abs < 45) return ` — ${dir} about a quarter since yesterday`;
  if (abs < 60) return ` — ${dir} about a third since yesterday`;
  return ` — ${dir} sharply since yesterday`;
};

const POPULATIONS = [
  { key: 'adults', label: '👨‍👩‍👧 Adults' },
  { key: 'kids',   label: '👶 Children' },
  { key: 'pets',   label: '🐕 Pets' },
];

export default function RiskHeroCard({ tierKey, pcTrend, habRisk, gridData }) {
  const [pop, setPop] = useState('adults');
  const [showExposure, setShowExposure] = useState(false);

  const tc = RISK_TIERS[tierKey] || RISK_TIERS.BASELINE;
  const trend = habRisk?.pc_trend || pcTrend;
  const chip = TREND_CHIP[trend] || TREND_CHIP.stable;
  const isHighRisk = tierKey === 'ADVISORY' || tierKey === 'WARNING';

  const change24h = changeToWords(habRisk?.pc_24h_change_pct);
  // visual_override_warning is a constant label string, not an on/off switch.
  // Drive the banner off the real active signal instead.
  // TODO: confirm field name with API team if neither candidate appears in the response.
  const hasOverride = !!(
    habRisk?.visual_override_active ||
    habRisk?.visual_override === true ||
    (Array.isArray(habRisk?.modifiers_applied) &&
      habRisk.modifiers_applied.some(m =>
        (typeof m === 'string' ? m : (m?.type ?? m?.name ?? '')).toLowerCase().includes('visual')
      ))
  );
  const guidance = getGuidance(tierKey, pop, trend);

  return (
    <div style={{ background: tc.bg, borderRadius: 14, padding: 24, border: `${tierKey === 'WARNING' ? 2 : 1}px solid ${tc.border}`, marginBottom: 16 }}>

      <HeroRiskScale risk={tierKey.toLowerCase()} trend={trend} />

      {/* Visual-override banner */}
      {hasOverride && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #f87171', marginBottom: 16 }}>
          <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.2 }}>🚩</span>
          <p style={{ fontSize: 13, color: '#b91c1c', lineHeight: 1.5, margin: 0 }}><strong>Scum or other warning signs reported on the lake.</strong> When scum, discoloration, an unusual odor, or dead fish are present, avoid water contact regardless of the estimate below.</p>
        </div>
      )}

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: tc.color, boxShadow: `0 0 12px ${tc.color}50` }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: tc.color }}>{gridData?.name || 'Pond'}</span>
        </div>
        {/* TODO(review): pill now duplicates HeroRiskScale — keep or drop? */}
        <div style={{ display: 'inline-flex', padding: '4px 12px', borderRadius: 100, fontSize: 12, fontWeight: 600, background: chip.bg, color: chip.color }}>
          {chip.text}{change24h}
        </div>
      </div>

      {/* Headline */}
      <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 28, fontWeight: 700, lineHeight: 1.2, color: tc.color, marginBottom: 6 }}>
        {getHeroHeadline(tierKey, trend)}
      </h2>
      <p style={{ fontSize: 13, color: tc.color, opacity: 0.85, marginBottom: 14, lineHeight: 1.45 }}>
        This is a sensor-based estimate, not a direct toxin measurement — and toxins can sometimes be present even when the water looks clear. Always judge conditions yourself at the water.
      </p>

      {/* Post-bloom note */}
      {habRisk?.post_bloom_elevated && habRisk?.days_since_bloom_peak != null && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(255,255,255,.7)', border: `1px solid ${tc.border}`, marginBottom: 14 }}>
          <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, margin: 0 }}>
            <strong>The water may look clearer than it did a few days ago.</strong> A bloom peaked here about {habRisk.days_since_bloom_peak} days ago, and as the cells die off they release toxins into the water. Risk stays high for a week or more after a bloom — even once the green color fades.
          </p>
        </div>
      )}

      {/* Downwind warning */}
      {habRisk?.downwind_shore_warning && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(0,0,0,.05)', marginBottom: 14 }}>
          <span style={{ fontSize: 15, flexShrink: 0 }}>🌬️</span>
          <p style={{ fontSize: 13, color: tc.color, lineHeight: 1.5, margin: 0 }}>{habRisk.downwind_shore_warning}</p>
        </div>
      )}

      {/* Population tabs */}
      <div style={{ display: 'flex', gap: 3, background: 'rgba(0,0,0,.04)', borderRadius: 10, padding: 3, marginBottom: 14 }}>
        {POPULATIONS.map(({ key, label }) => (
          <button key={key} onClick={() => setPop(key)} style={{
            flex: 1, padding: '9px 4px', borderRadius: 8, fontSize: 13, textAlign: 'center', cursor: 'pointer', border: 'none',
            background: pop === key ? '#fff' : 'transparent',
            color: pop === key ? tc.color : '#64748b',
            fontWeight: pop === key ? 600 : 400,
            boxShadow: pop === key ? '0 1px 4px rgba(0,0,0,.07)' : 'none',
          }}>{label}</button>
        ))}
      </div>

      {/* Guidance text */}
      <div style={{ padding: 16, borderRadius: 10, background: 'rgba(255,255,255,.7)' }}>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: '#374151', margin: 0 }}>{guidance}</p>
      </div>
      <p style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.4, margin: '8px 2px 0' }}>
        General guidance based on current estimates — not a safety guarantee for your specific spot or time.
      </p>

      {/* Exposure — collapsible, high risk only */}
      {isHighRisk && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => setShowExposure(v => !v)} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
            padding: '10px 14px', borderRadius: showExposure ? '10px 10px 0 0' : 10,
            background: 'rgba(255,255,255,.85)', border: `1px solid ${tc.border}`, cursor: 'pointer',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: tc.color }}>If someone is exposed — what to do</span>
            <span style={{ fontSize: 14, color: tc.color, opacity: 0.6, display: 'inline-block', transform: showExposure ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▾</span>
          </button>
          {showExposure && (
            <div style={{ padding: '12px 14px', borderRadius: '0 0 10px 10px', background: 'rgba(255,255,255,.85)', border: `1px solid ${tc.border}`, borderTop: 'none' }}>
              <p style={{ fontSize: 15, lineHeight: 1.65, color: '#374151', margin: '0 0 8px 0' }}>
                <strong>People:</strong> Rinse skin thoroughly with clean water. Watch for rash, nausea, vomiting, or diarrhea. Seek medical attention if symptoms develop.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.65, color: '#374151', margin: 0 }}>
                <strong>Pets:</strong> Rinse immediately and prevent licking fur. Watch for drooling, vomiting, weakness, or seizures. <strong>Contact your vet right away.</strong>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,.06)' }}>
        {/* Visual override disclaimer */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: habRisk?.confidence_score != null ? 8 : 0 }}>
          <span style={{ fontSize: 15, flexShrink: 0 }}>{isHighRisk ? '⚠️' : '👀'}</span>
          <p style={{ fontSize: 15, color: isHighRisk ? tc.color : '#46505a', lineHeight: 1.55, margin: 0 }}>
            {isHighRisk
              ? <><strong>Even if the water looks clear at your spot,</strong> sensor data indicates conditions are unsafe across the pond.</>
              : <><strong style={{ color: '#475569' }}>Trust your eyes over the app.</strong> If you see green water, surface scum, or smell something off — stay out, no matter what this estimate says.</>}
          </p>
        </div>

        {/* Model metadata — demoted, available on hover */}
        {habRisk?.confidence_score != null && (
          <p
            title={`Model confidence: ${Math.round(habRisk.confidence_score * 100)}%${habRisk.model_version ? ` · v${habRisk.model_version}` : ''}`}
            style={{ fontSize: 12, color: '#8b8475', margin: 0, cursor: 'default' }}
          >
            Sensor-based estimate · see "How is this estimated?" to learn more
          </p>
        )}
      </div>
    </div>
  );
}
